import { Box, Flex, Kbd, Modal, ModalBody, ModalContent, ModalOverlay, Text, useDisclosure } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { removeAccents, useHasRole } from '../../helpers/index'
import { Icon } from '../Icon'
import { AiSparkleIcon } from '../icons'
import { INavbarGroup, INavbarItem, navbarItems } from './navbarItems'

interface ISpotlightOption {
  title: string
  path: string
  icon?: string
  group?: string
}

const RECENT_STORAGE_KEY = 'spotlight_recent_paths'
const MAX_RECENT = 5

// Se o usuário estiver digitando em um campo, o atalho '/' não deve abrir o spotlight.
const isTypingElement = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable
}

const normalize = (value: string): string => removeAccents(value).toLowerCase().trim()

const readRecentPaths = (): string[] => {
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : []
  } catch {
    return []
  }
}

export default function SpotlightSearch() {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const hasSystemRole = useHasRole()

  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [recentPaths, setRecentPaths] = useState<string[]>(readRecentPaths)

  const inputRef = useRef<HTMLInputElement>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])

  // Achata os grupos da sidebar em uma lista pesquisável, respeitando as permissões do usuário.
  const options = useMemo<ISpotlightOption[]>(() => {
    const byPath = new Map<string, ISpotlightOption>()

    const pushOption = (option: ISpotlightOption) => {
      const existing = byPath.get(option.path)
      if (existing) {
        // Rota presente em mais de um grupo: agrega os nomes para a busca por grupo funcionar.
        if (option.group && existing.group && !existing.group.includes(option.group)) {
          existing.group = `${existing.group} / ${option.group}`
        }
        return
      }
      byPath.set(option.path, { ...option })
    }

    navbarItems.forEach((item) => {
      if ('items' in item) {
        const group = item as INavbarGroup
        group.items.forEach((subItem) => {
          if (subItem.roles.length === 0 || hasSystemRole(subItem.roles)) {
            pushOption({
              title: subItem.title,
              path: subItem.path,
              icon: subItem.icon ?? group.icon,
              group: group.title
            })
          }
        })
      } else {
        const single = item as INavbarItem
        if (single.roles.length === 0 || hasSystemRole(single.roles)) {
          pushOption({ title: single.title, path: single.path, icon: single.icon })
        }
      }
    })

    return Array.from(byPath.values())
  }, [hasSystemRole])

  const recentOptions = useMemo<ISpotlightOption[]>(() => {
    return recentPaths
      .map((path) => options.find((option) => option.path === path))
      .filter((option): option is ISpotlightOption => Boolean(option))
  }, [recentPaths, options])

  const filteredOptions = useMemo<ISpotlightOption[]>(() => {
    const normalizedQuery = normalize(query)
    if (!normalizedQuery) return recentOptions

    // Cada termo pode casar em qualquer parte (título ou grupo): "comprovantes vendas" acha "Comprovantes de Vendas".
    const terms = normalizedQuery.split(/\s+/).filter(Boolean)
    return options.filter((option) => {
      const haystack = normalize(`${option.title} ${option.group ?? ''}`)
      return terms.every((term) => haystack.includes(term))
    })
  }, [query, options, recentOptions])

  const isShowingRecent = normalize(query).length === 0

  // Registra a navegação real (sidebar, link direto ou spotlight) para alimentar os "recentes".
  useEffect(() => {
    if (!options.some((option) => option.path === pathname)) return
    setRecentPaths((prev) => {
      const updated = [pathname, ...prev.filter((path) => path !== pathname)].slice(0, MAX_RECENT)
      try {
        localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(updated))
      } catch {
        // Ignora falhas de storage (ex: modo privativo).
      }
      return updated
    })
  }, [pathname, options])

  // Reseta a seleção ao trocar a busca ou reabrir o modal, evitando 'Enter' cair em item errado.
  useEffect(() => {
    setActiveIndex(0)
  }, [query, isOpen])

  // Mantém o item selecionado visível durante a navegação por setas.
  useEffect(() => {
    itemRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, filteredOptions])

  // Atalho global: '/' abre o spotlight (exceto ao digitar, com modificadores ou sobre outro diálogo).
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || isOpen) return
      if (event.ctrlKey || event.metaKey || event.altKey) return
      if (isTypingElement(event.target)) return
      // Não sequestra o '/' quando há outro diálogo aberto por cima (evita descartar formulários).
      if (event.target instanceof HTMLElement && event.target.closest('[role="dialog"]')) return

      event.preventDefault()
      onOpen()
    }

    // Permite abrir o spotlight via clique (ex: atalho no rodapé da sidebar).
    const handleOpenEvent = () => onOpen()

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('open-spotlight', handleOpenEvent)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('open-spotlight', handleOpenEvent)
    }
  }, [isOpen, onOpen])

  const handleSelect = useCallback(
    (option: ISpotlightOption | undefined) => {
      if (!option) return
      onClose()
      setQuery('')
      navigate(option.path)
    },
    [navigate, onClose]
  )

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((prev) => (filteredOptions.length === 0 ? 0 : (prev + 1) % filteredOptions.length))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((prev) =>
        filteredOptions.length === 0 ? 0 : (prev - 1 + filteredOptions.length) % filteredOptions.length
      )
    } else if (event.key === 'Enter') {
      event.preventDefault()
      handleSelect(filteredOptions[activeIndex])
    }
  }

  const handleModalClose = () => {
    setQuery('')
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} initialFocusRef={inputRef} isCentered size="xl">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(6px)" />
      <ModalContent
        bg="darkGray.700"
        color="#fff"
        borderRadius="16px"
        overflow="hidden"
        mx={4}
        boxShadow="0 20px 60px rgba(0,0,0,0.55)"
      >
        <ModalBody p={0}>
          <Flex align="center" px={5} py={4} borderBottom="1px solid" borderColor="darkGray.500">
            <Icon name="lni lni-search-alt" size="lg" color="abraGray.50" mr={3} />
            <input
              ref={inputRef}
              role="combobox"
              aria-label="Buscar no menu"
              aria-expanded={isOpen}
              aria-controls="spotlight-listbox"
              aria-activedescendant={filteredOptions.length > 0 ? `spotlight-option-${activeIndex}` : undefined}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Buscar no menu..."
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: '#fff',
                fontSize: '18px'
              }}
            />
            <Kbd bg="darkGray.500" color="darkGray.400" borderColor="abraGray.400">
              esc
            </Kbd>
          </Flex>

          <Box id="spotlight-listbox" role="listbox" maxH="360px" overflowY="auto" py={2}>
            {isShowingRecent && recentOptions.length > 0 && (
              <Text
                px={5}
                py={2}
                fontSize="xs"
                fontWeight="600"
                textTransform="uppercase"
                color="darkGray.400"
                letterSpacing="0.5px"
              >
                Visitados recentemente
              </Text>
            )}

            {filteredOptions.length === 0 && (
              <Text px={5} py={6} color="abraGray.50" textAlign="center">
                {isShowingRecent ? 'Digite para buscar uma página do menu' : 'Nenhum resultado encontrado'}
              </Text>
            )}

            {filteredOptions.map((option, index) => (
              <Flex
                key={option.path}
                ref={(el) => (itemRefs.current[index] = el)}
                role="option"
                id={`spotlight-option-${index}`}
                aria-selected={index === activeIndex}
                align="center"
                px={5}
                py={3}
                cursor="pointer"
                bg={index === activeIndex ? 'rgba(250, 0, 105, 0.18)' : 'transparent'}
                borderLeft="3px solid"
                borderColor={index === activeIndex ? 'magenta.500' : 'transparent'}
                transition="background 0.1s ease-in-out"
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => handleSelect(option)}
              >
                {option.icon === 'custom:ai-sparkle' ? (
                  <AiSparkleIcon style={{ marginRight: 14 }} />
                ) : (
                  <Icon name={option.icon ?? 'lni lni-grid-alt'} size="lg" color="darkGray.300" mr={3.5} w="18px" />
                )}
                <Box flex={1}>
                  <Text fontSize="15px">{option.title}</Text>
                  {option.group && (
                    <Text fontSize="12px" color="darkGray.400">
                      {option.group}
                    </Text>
                  )}
                </Box>
                {index === activeIndex && (
                  <Kbd bg="darkGray.500" color="darkGray.400" borderColor="abraGray.400">
                    ↵
                  </Kbd>
                )}
              </Flex>
            ))}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
